import React, { useState } from 'react';
import {
  Badge, Button, ConfirmDialog, DataTable, Input, Modal, PageHead, Switch, num,
} from '../components/ui';
import useList from '../hooks/useList';
import { materialApi } from '../api';
import { useToast } from '../context/ToastContext';

const emptyForm = { code: '', label: '', description: '', sortOrder: 0, isActive: true };

/**
 * The fabrics a seller can specify — HDPE, PVC, canvas and whatever the trade
 * adopts next. Curated here so a new material needs no deploy.
 */
const Materials = () => {
  const toast = useToast();
  const list = useList(materialApi.list, {});

  const [form, setForm] = useState(null);
  const [retire, setRetire] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.label.trim()) return toast.error('Give the material a label');
    if (!form._id && !form.code.trim()) return toast.error('Give the material a code');

    setBusy(true);
    try {
      if (form._id) await materialApi.update(form._id, form);
      else await materialApi.create(form);
      toast.success(form._id ? 'Material updated' : 'Material added');
      setForm(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const doRetire = async () => {
    setBusy(true);
    try {
      const res = await materialApi.remove(retire._id);
      toast.success(res.message);
      setRetire(null);
      list.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'label',
      label: 'Material',
      render: row => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.label}</div>
          <div className="text-sm text-muted">{row.description || '—'}</div>
        </div>
      ),
    },
    { key: 'code', label: 'Code', render: row => <code>{row.code}</code> },
    { key: 'sortOrder', label: 'Order', align: 'center' },
    {
      key: 'isActive',
      label: 'Status',
      render: row => <Badge status={row.isActive ? 'active' : 'draft'}>{row.isActive ? 'Offered' : 'Retired'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          <Button size="sm" variant="ghost" onClick={() => setForm({ ...row })}>Edit</Button>
          {row.isActive && <Button size="sm" variant="danger" onClick={() => setRetire(row)}>Retire</Button>}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Materials"
        subtitle="The fabrics sellers can specify on a listing — HDPE, PVC, canvas and anything else you add."
        actions={<Button icon="＋" onClick={() => setForm({ ...emptyForm })}>New material</Button>}
      />

      <div className="filters">
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} materials` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} />

      <Modal
        open={!!form}
        title={form?._id ? 'Edit material' : 'New material'}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
            <Button loading={busy} onClick={submit}>{form?._id ? 'Save changes' : 'Add material'}</Button>
          </>
        }
      >
        {form && (
          <>
            <Input
              label="Code"
              value={form.code}
              disabled={!!form._id}
              onChange={e => setForm({ ...form, code: e.target.value })}
              placeholder="HDPE"
              hint={
                form._id
                  ? 'The code is fixed once products are specified in this material.'
                  : 'Uppercase, no spaces. Stored on every product that uses it.'
              }
            />
            <Input
              label="Label"
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="HDPE"
            />
            <Input
              label="Description"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="High-density polyethylene woven fabric"
              hint="Shown to sellers under the option."
            />
            <Input
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm({ ...form, sortOrder: Number(e.target.value) })}
              hint="Lower numbers appear first in the seller's list."
            />
            <Switch
              checked={form.isActive}
              onChange={v => setForm({ ...form, isActive: v })}
              label="Offer this material to sellers"
            />
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!retire}
        title="Retire this material?"
        message={`"${retire?.label}" will stop appearing when sellers list a product. Products already made of it keep working.`}
        confirmLabel="Retire"
        loading={busy}
        onConfirm={doRetire}
        onClose={() => setRetire(null)}
      />
    </>
  );
};

export default Materials;
